import { findEmptyBlockOccFrom } from './pico8Sheet';

export type SheetSprite = { pixels: number[][]; palette?: string[] };

type Options = {
  scale?: number; // 1 px per sprite-px; exportbredd = 128*scale
  background?: 'transparent' | 'black';
  primaryColors: string[];      // krävs för färgupplösning
  secondaryColors?: string[];   // optional: alt-palett
};

/**
 * Packar sprites på ett 128×128-ark (PICO-8-layout) och returnerar PNG Blob + dataURL.
 * - Behåller secondary-index (128..143/-16..-1) vid rendering.
 * - Index 0 ritas transparent om background='transparent', annars på svart bakgrund.
 */
export async function spritesToPngSheet(
  sprites: SheetSprite[],
  { scale = 4, background = 'transparent', primaryColors, secondaryColors }: Options
): Promise<{ blob: Blob; dataUrl: string }> {
  // 1) Bygg occupancy 16×16 tiles och en 128×128 grid med “fulla” index (inkl. secondary)
  const T = 16;
  const GRID = 128;
  const occ = Array.from({ length: T }, () => Array(T).fill(false));
  const grid = Array.from({ length: GRID }, () => Array(GRID).fill(0));

  // Sortera 16×16 först för bättre packning
  const ordered = [...sprites].sort((a, b) => {
    const sa = a.pixels?.length === 16 || a.pixels?.[0]?.length === 16 ? 1 : 0;
    const sb = b.pixels?.length === 16 || b.pixels?.[0]?.length === 16 ? 1 : 0;
    return sb - sa;
  });

  let scanStart = 0;
  for (const s of ordered) {
    const p = s.pixels || [];
    const h = p.length || 8;
    const w = p[0]?.length || h;
    const size: 8 | 16 = (w === 16 || h === 16) ? 16 : 8;
    const needW = size === 16 ? 2 : 1;
    const needH = size === 16 ? 2 : 1;

    const pos = findEmptyBlockOccFrom(occ, needW, needH, scanStart);
    if (!pos) break;

    // mark tiles som använda
    for (let by = 0; by < needH; by++)
      for (let bx = 0; bx < needW; bx++)
        occ[pos.ty + by][pos.tx + bx] = true;

    for (let y = 0; y < size && y < h; y++) {
      for (let x = 0; x < size && x < w; x++) {
        const v = p[y]?.[x] ?? 0; // behåll original (0..15 / 128..143 / -16..-1)
        grid[pos.ty * 8 + y][pos.tx * 8 + x] = v;
      }
    }

    scanStart = pos.ty * 16 + (pos.tx + needW);
    if (scanStart >= 256) scanStart = 0;
  }

  // 2) Rendera grid → canvas
  const s = Math.max(1, Math.floor(scale));
  const W = GRID * s;
  const H = GRID * s;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  if (!ctx) throw new Error('2D context missing');
  ctx.imageSmoothingEnabled = false;

  if (background === 'black') {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
  } else {
    ctx.clearRect(0, 0, W, H);
  }

  const resolve = (ci: number): string | null => {
    // 0 => transparent om transparent bakgrund
    if (ci === 0 && background === 'transparent') return null;
    const isSec = (ci & 0x80) !== 0 || ci < 0;
    const base = ci < 0 ? (ci + 16) & 0x0f : (ci & 0x0f);
    const sec = secondaryColors ?? primaryColors;
    return isSec ? (sec[base] ?? primaryColors[base] ?? '#000') : (primaryColors[base] ?? '#000');
  };

  for (let y = 0; y < GRID; y++) {
    for (let x = 0; x < GRID; x++) {
      const ci = grid[y][x] ?? 0;
      const color = resolve(ci);
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x * s, y * s, s, s);
    }
  }

  // 3) Exportera Blob + dataURL
  const blob = await new Promise<Blob>((res, rej) =>
    cv.toBlob(b => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/png') as any
  );
  const dataUrl = cv.toDataURL('image/png');
  return { blob, dataUrl };
}